import type { HttpTypes } from '@medusajs/types';

import { getPercentageDiff } from './get-percentage-diff';
import { convertToLocale } from './money';

interface VariantPrices {
  calculated_price_number: number;
  calculated_price: string;
  original_price_number: number;
  original_price: string;
  suggested_price: string;
  member_price: string;
  currency_code: string;
  price_type: string | null;
  percentage_diff: string;
}

/**
 * Get formatted prices for a product variant.
 * @param variant - Product variant with calculated_price
 * @returns Formatted price object or null if no price available
 */
export const getPricesForVariant = (
  variant: HttpTypes.StoreProductVariant | HttpTypes.StoreCartLineItem['variant'] | null | undefined
): VariantPrices | null => {
  // Type guard: check if variant has calculated_price
  const calculatedPrice = (variant as { calculated_price?: HttpTypes.StoreProductVariant['calculated_price'] })
    ?.calculated_price;

  if (!calculatedPrice?.calculated_amount) {
    return null;
  }

  const metadata = (variant as { metadata?: Record<string, unknown> })?.metadata;

  return {
    calculated_price_number: calculatedPrice.calculated_amount,
    calculated_price: convertToLocale({
      amount: calculatedPrice.calculated_amount,
      currency_code: calculatedPrice.currency_code ?? 'TWD',
    }),
    original_price_number: calculatedPrice.original_amount ?? calculatedPrice.calculated_amount,
    original_price: convertToLocale({
      amount: calculatedPrice.original_amount ?? calculatedPrice.calculated_amount,
      currency_code: calculatedPrice.currency_code ?? 'TWD',
    }),
    suggested_price: convertToLocale({
      amount: (metadata?.suggested_price as number) ?? 0,
      currency_code: calculatedPrice.currency_code ?? 'TWD',
    }),
    member_price: convertToLocale({
      amount: (metadata?.member_price as number) ?? 0,
      currency_code: calculatedPrice.currency_code ?? 'TWD',
    }),
    currency_code: calculatedPrice.currency_code ?? 'TWD',
    price_type: (calculatedPrice as { price_list_type?: string | null })
      ?.price_list_type ?? null,
    percentage_diff: getPercentageDiff(
      calculatedPrice.original_amount ?? calculatedPrice.calculated_amount,
      calculatedPrice.calculated_amount
    ),
  };
};

interface ProductPriceResult {
  product: HttpTypes.StoreProduct;
  cheapestPrice: VariantPrices | null;
  variantPrice: VariantPrices | null;
}

/**
 * Get product price information including cheapest variant and specific variant prices.
 * @param product - Product to get prices for
 * @param variantId - Optional specific variant ID or SKU
 * @returns Product with cheapest and variant prices
 */
export function getProductPrice({
  product,
  variantId,
}: {
  product: HttpTypes.StoreProduct;
  variantId?: string;
}): ProductPriceResult {
  if (!product || !product.id) {
    throw new Error('No product provided');
  }

  const cheapestPrice = (): VariantPrices | null => {
    if (!product || !product.variants?.length) {
      return null;
    }

    const variantsWithPrice = product.variants.filter(
      (v) => (v as { calculated_price?: unknown }).calculated_price
    );

    if (variantsWithPrice.length === 0) {
      return null;
    }

    const sortedVariants = [...variantsWithPrice].sort((a, b) => {
      const aPrice = (a as { calculated_price?: { calculated_amount?: number } }).calculated_price
        ?.calculated_amount ?? 0;
      const bPrice = (b as { calculated_price?: { calculated_amount?: number } }).calculated_price
        ?.calculated_amount ?? 0;
      return aPrice - bPrice;
    });

    return getPricesForVariant(sortedVariants[0]);
  };

  const variantPrice = (): VariantPrices | null => {
    if (!product || !variantId) {
      return null;
    }

    const variant = product.variants?.find((v) => v.id === variantId || v.sku === variantId);

    if (!variant) {
      return null;
    }

    return getPricesForVariant(variant);
  };

  return {
    product,
    cheapestPrice: cheapestPrice(),
    variantPrice: variantPrice(),
  };
}
